import React, { useEffect, useState } from "react";
import { useLocation, NavLink } from "react-router-dom";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavCard from "examples/Sidenav/SidenavCard";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import {
  collapseItem,
  collapseIconBox,
  collapseText,
} from "examples/Sidenav/styles/sidenavCollapse";
import { useSoftUIController, setMiniSidenav } from "context";
import { useSelector } from "react-redux";
import "../../assets/custom_styles.scss";
import { buildClientLogoUrl } from "utils/clientLogoUrl";
import { contrastTextForHex } from "utils/colorContrast";

interface RouteType {
  type?: "collapse" | "title" | "divider" | (string & {});
  name?: string;
  icon?: React.ReactNode | string;
  title?: string;
  noCollapse?: boolean;
  key?: string;
  route?: string;
  href?: string;
  roles?: string[];
  adminMainNav?: boolean;
  collapse?: RouteType[];
  color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark";
}

interface UserType {
  Role: string[];
}

interface SidenavProps {
  /** CDN key for client logo (same as API `client_logo_url`) */
  logo?: string | React.ReactNode;
  color?:
    | "primary"
    | "secondary"
    | "info"
    | "success"
    | "warning"
    | "error"
    | "dark";
  brand?: string | React.ReactNode;
  brandName: string;
  /** Client theme hex for letter avatar / contrast */
  themeColor?: string;
  routes: RouteType[];
  [key: string]: any;
}

function renderItemIcon(icon: RouteType["icon"]) {
  if (!icon) return null;
  if (typeof icon === "string") return <Icon>{icon}</Icon>;
  return <>{icon}</>;
}

function renderCollapseChild(
  item: RouteType,
  pathname: string,
  miniSidenav: boolean,
  transparentSidenav: boolean,
  iconColor: string,
  userRole: string
) {
  if (item.type === "title") {
    return (
      <SoftTypography
        key={item.key}
        variant="caption"
        color="dark"
        sx={{
          display: "block",
          fontWeight: "bold",
          textTransform: "uppercase",
          color: "#344767 !important",
          pl: 1.5,
          mt: 1,
          mb: 0.5,
          fontSize: "0.75rem",
        }}
      >
        {item.title}
      </SoftTypography>
    );
  }
  if (
    item.type === "collapse" &&
    item.route &&
    (!item.roles || item.roles.includes(userRole))
  ) {
    const itemActive = pathname === item.route;
    return (
      <NavLink to={item.route} key={item.key} style={{ textDecoration: "none" }}>
        <ListItem sx={{ py: 0, minHeight: "unset", mt: 0.75 }}>
          <SoftBox
            sx={(theme: any) => ({
              ...collapseItem(theme, { active: itemActive, transparentSidenav }),
              display: "flex",
              alignItems: "center",
              width: "100%",
            })}
          >
            <ListItemIcon
              sx={(theme: any) =>
                collapseIconBox(theme, {
                  active: itemActive,
                  transparentSidenav,
                  color: iconColor,
                })
              }
            >
              {renderItemIcon(item.icon)}
            </ListItemIcon>
            <ListItemText
              primary={item.name}
              sx={(theme: any) =>
                collapseText(theme, {
                  miniSidenav,
                  transparentSidenav,
                  active: itemActive,
                })
              }
            />
          </SoftBox>
        </ListItem>
      </NavLink>
    );
  }
  return null;
}

const Sidenav: React.FC<SidenavProps> = ({
  logo,
  color = "secondary",
  brand: _brand,
  brandName,
  themeColor = "#2e7d32",
  routes,
  ...rest
}) => {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const [logoBroken, setLogoBroken] = useState(false);

  const logoStringKey = typeof logo === "string" ? logo : "";
  const logoUrl = logoStringKey ? buildClientLogoUrl(logoStringKey) : null;

  useEffect(() => {
    setLogoBroken(false);
  }, [logoUrl]);

  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];

  const user = useSelector((state: any) => state.auth.user) as UserType;

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    const handleMiniSidenav = () => {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    };

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();

    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const adminRoles = ["admin", "supervisor", "superAdmin"];
  const isAdminRole = user?.Role?.[0] && adminRoles.includes(user.Role[0]);
  const [openCollapse, setOpenCollapse] = useState<string | null>(null);

  const renderRoutes = routes.map(
    ({
      type,
      name,
      icon,
      title,
      noCollapse,
      key,
      route,
      href,
      roles,
      adminMainNav,
      collapse: collapseItems,
      color: routeColor,
    }) => {
      const iconColor = routeColor ?? color;
      const showInSidenav =
        type === "collapse" &&
        roles?.includes(user.Role[0]) &&
        (!isAdminRole ||
          adminMainNav === true ||
          (collapseItems?.length && noCollapse === false));

      if (showInSidenav) {
        if (collapseItems?.length && noCollapse === false) {
          const isOpen = openCollapse === key;
          const isActive = collapseItems.some(
            (c) => c.route && pathname === c.route
          );
          return (
            <React.Fragment key={key}>
              <ListItem
                component="div"
                sx={{ cursor: "pointer", p: 0 }}
                onClick={() => setOpenCollapse(isOpen ? null : key!)}
              >
                <SoftBox
                  sx={(theme: any) => ({
                    ...collapseItem(theme, { active: isActive, transparentSidenav }),
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                  })}
                >
                  <ListItemIcon
                    sx={(theme: any) =>
                      collapseIconBox(theme, {
                        active: isActive,
                        transparentSidenav,
                        color: iconColor,
                      })
                    }
                  >
                    {typeof icon === "string" ? <Icon>{icon}</Icon> : icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={name}
                    sx={(theme: any) =>
                      collapseText(theme, {
                        miniSidenav,
                        transparentSidenav,
                        active: isActive,
                      })
                    }
                  />
                  <Icon
                    sx={(theme: any) => ({
                      ml: "auto",
                      mr: 1,
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s",
                      fontSize: 20,
                      [theme.breakpoints.up("xl")]: {
                        opacity: miniSidenav ? 0 : 1,
                        maxWidth: miniSidenav ? 0 : "1em",
                        overflow: "hidden",
                        margin: miniSidenav ? 0 : undefined,
                      },
                    })}
                  >
                    expand_more
                  </Icon>
                </SoftBox>
              </ListItem>
              <Collapse in={isOpen} unmountOnExit>
                <List disablePadding sx={{ pl: 2.5 }}>
                  {collapseItems.map((item) =>
                    renderCollapseChild(
                      item,
                      pathname,
                      miniSidenav,
                      transparentSidenav,
                      iconColor,
                      user.Role[0]
                    )
                  )}
                </List>
              </Collapse>
            </React.Fragment>
          );
        }

        return href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              {...(user.Role.includes("stockManager") && { className: "stock" })}
              color={iconColor}
              name={name!}
              icon={icon!}
              active={key === collapseName}
              noCollapse={noCollapse}
            />
          </Link>
        ) : (
          <NavLink to={route!} key={key}>
            <SidenavCollapse
              {...(user.Role.includes("stockManager") && { className: "stock" })}
              color={iconColor}
              key={key}
              name={name!}
              icon={icon!}
              active={key === collapseName}
              noCollapse={noCollapse}
            />
          </NavLink>
        );
      }

      if (type === "title") {
        return (
          <SoftTypography
            key={key}
            variant="caption"
            sx={{
              display: "block",
              fontWeight: "bold",
              textTransform: "uppercase",
              opacity: 0.6,
              pl: 3,
              mt: 2,
              mb: 1,
              ml: 1,
            }}
          >
            {title}
          </SoftTypography>
        );
      }

      if (type === "divider") {
        return <Divider key={key} />;
      }

      return null;
    }
  );

  return (
    <SidenavRoot
      {...(user.Role.includes("stockManager") && { className: "stock" })}
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, miniSidenav }}
    >
      <SoftBox pt={1} pb={1} px={4} textAlign="center">
        <SoftBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <SoftTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </SoftTypography>
        </SoftBox>
        <SoftBox display="flex" alignItems="center" className="dashboard-logo">
          <SoftBox
            component={Link}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {logoUrl && !logoBroken ? (
              <Box
                component="img"
                src={logoUrl}
                alt=""
                onError={() => setLogoBroken(true)}
                sx={{
                  maxHeight: 52,
                  maxWidth: miniSidenav ? 40 : 200,
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: miniSidenav ? 36 : 48,
                  height: miniSidenav ? 36 : 48,
                  bgcolor: themeColor,
                  color: contrastTextForHex(themeColor),
                  fontWeight: 700,
                  fontSize: miniSidenav ? "1rem" : "1.25rem",
                }}
              >
                {(brandName || "?").trim().charAt(0).toUpperCase() || "?"}
              </Avatar>
            )}
          </SoftBox>
          <SoftBox
            className="hidden"
            width={!brandName ? "100%" : undefined}
            sx={(theme: any) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <SoftTypography
              sx={{ component: "h6" }}
              variant="button"
              fontWeight="medium"
            >
              {brandName}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      </SoftBox>

      <Divider sx={{ margin: "0.25rem 0px" }} />
      <List>{renderRoutes}</List>

      <SoftBox pt={2} my={2} mx={2} mt="auto">
        <SidenavCard />
        <SoftBox mt={2}></SoftBox>
      </SoftBox>
    </SidenavRoot>
  );
};

export default Sidenav;